import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    // Listen for cart updates to auto-apply coupon codes
    document.addEventListener('cart:update', this.#handleCartProgressDiscount);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:update', this.#handleCartProgressDiscount);
  }

  // Auto-apply coupon codes based on cart item count
  /**
   * Auto-apply coupon codes based on cart item count
   * @param {any} event
   */
  #handleCartProgressDiscount = (event) => {
    // Try to get item count from event, fallback to DOM
    let itemCount = 0;
    if (event && event.detail && event.detail.data && typeof event.detail.data.itemCount === 'number') {
      itemCount = event.detail.data.itemCount;
    } else {
      // Fallback: try to read from hidden span in cart drawer
      const countEl = document.querySelector('.cart-drawer [ref="cartItemCount"]');
      if (countEl) {
        itemCount = parseInt(countEl.textContent, 10) || 0;
      }
    }

    let code = '';
    if (itemCount >= 3) {
      code = 'BUY3GET400';
    } else if (itemCount === 2) {
      code = 'BUY2GET200';
    }

    // Find the discount form in the cart drawer
    const discountForm = document.querySelector('.cart-drawer .cart-discount__form');
    const discountInput = discountForm?.querySelector('input[name="discount"]');
    if (!discountForm || !(discountInput instanceof HTMLInputElement)) return;

    // Get currently applied codes from pills
    const appliedCodes = Array.from(document.querySelectorAll('.cart-drawer .cart-discount__pill')).map(pill => (pill instanceof HTMLElement ? pill.dataset.discountCode : undefined)).filter(Boolean);

    // If the right code is already applied, do nothing
    if (code && appliedCodes.includes(code) && appliedCodes.length === 1) return;

    // Remove all codes if no code should be applied
    if (!code && appliedCodes.length > 0) {
      // Remove all applied codes
      appliedCodes.forEach(() => {
        const removeBtn = document.querySelector('.cart-drawer .cart-discount__pill-remove');
        if (removeBtn && removeBtn instanceof HTMLElement && typeof removeBtn.click === 'function') removeBtn.click();
      });
      return;
    }

    // If a different code is applied, remove it first
    if (code && (!appliedCodes.includes(code) || appliedCodes.length > 1)) {
      // Remove all other codes
      appliedCodes.forEach(applied => {
        if (applied !== code) {
          const pill = document.querySelector(`.cart-drawer .cart-discount__pill[data-discount-code="${applied}"]`);
          const removeBtn = pill?.querySelector('.cart-discount__pill-remove');
          if (removeBtn && removeBtn instanceof HTMLElement && typeof removeBtn.click === 'function') removeBtn.click();
        }
      });
      // Wait a tick for UI to update, then apply the right code
      setTimeout(() => {
        discountInput.value = code;
        const submitBtn = discountForm.querySelector('button[type="submit"]');
        if (submitBtn && submitBtn instanceof HTMLElement && typeof submitBtn.click === 'function') submitBtn.click();
      }, 300);
      return;
    }

    // If no code is applied and we need to apply one
    if (code && appliedCodes.length === 0) {
      discountInput.value = code;
      const submitBtn = discountForm.querySelector('button[type="submit"]');
      if (submitBtn && submitBtn instanceof HTMLElement && typeof submitBtn.click === 'function') submitBtn.click();
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
